import React, {useEffect, useRef, useState} from 'react'
import ReactDOM from 'react-dom'
import {Box, Button, Text, Tooltip} from '@chakra-ui/react'
import {Divider, IconButton} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import {useIntl, defineMessages} from 'react-intl'
import {
    getGDIDesktopHoverSx,
    getGDINestedResponsiveSx,
    getGDIHideStyles,
    mergeGDISx,
    pickGDIDomProps,
    resolveGDIStyles,
    splitGDIResponsiveStyles,
    GDI_TEXT_BLOCK_STYLE_MAP,
    GDI_INLINE_MAP_SX,
    resolveResponsiveFontStyleProps,
    mergeResolvedStyles
} from '../../utils/gdi-styles'
import {parseShortcodes} from '../../utils/gdi-shortcode-parser'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../../../../utils/site-config'
import {localizeHref} from '../../../../../utils/url'
import {useBrand} from '../../../../../contexts/brand-context'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useGDIDynamicContentContext} from '../../utils/gdi-dynamic-content-context'
import {useGDIRewardTileContext} from '../../utils/gdi-reward-tile-context'
import {useCustomToast} from '../../../../../contexts/custom-toast-context'
import {InfoCircle, CopyIcon} from '../../../../../components/icons'
import NewsletterFormInline from '../../../../../pages/my-rewards/partials/newsletter-form-inline'
import GDIInlineMap from '../../common/gdiInlineMap'
import GDIRangeMarker from '../../common/gdiRangeMarker'

const voucherMessages = defineMessages({
    copyAriaLabel: {
        id: 'gdi.voucher.copy_aria_label',
        defaultMessage: 'Copy promo code {code}'
    },
    promoCodeCopied: {
        id: 'gdi.voucher.promo_code_copied',
        defaultMessage: 'Promo code copied'
    }
})

const newsletterMessages = defineMessages({
    subscribedToast: {
        id: 'rewards.newsletter_subscribe.toast_success',
        defaultMessage: 'Subscribed to Newsletter'
    }
})

// Drawer-typed shortcode links are rendered as `<a data-gdi-drawer="...">`.
// A global document-level click handler in the locale `_app` intercepts those
// clicks and dispatches `gdi:openDrawer` window events; the relevant
// modal/drawer providers listen for that event to open themselves.

const TAG_TEXT_STYLES = {
    '& p': {textStyle: 'bodySmRegular'},
    '& h1': {textStyle: 'webH1'},
    '& h2': {textStyle: 'webH2'},
    '& h3': {textStyle: 'webH3'},
    '& h4, & h5, & h6': {textStyle: 'webH4'},
    // Restore list styling overridden by Chakra's CSS reset (list-style:none; padding:0).
    // paddingLeft positions the marker inside the ul/ol box so it stays visible even
    // when a parent (e.g. GDICollapsibleItemBlock's Collapse) has overflow:hidden.
    '& ul': {paddingLeft: '10px', listStyle: 'none'},
    '& ul li': {display: 'flex', alignItems: 'flex-start', gap: '10px', lineHeight: '24px'},
    '& ul li::before': {
        content: '""',
        display: 'block',
        flexShrink: 0,
        flexGrow: 0,
        flexBasis: '3px',
        alignSelf: 'flex-start',
        marginTop: '0.6em',
        width: '3px',
        height: '3px',
        overflow: 'hidden',
        transform: 'translateZ(0)',
        backgroundColor: 'webTypo.textPrimary'
    },
    '& ol': {paddingLeft: '10px', listStyle: 'none', counterReset: 'gdi-ordered-list'},
    '& ol li': {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        lineHeight: '24px',
        counterIncrement: 'gdi-ordered-list'
    },
    '& ol li::before': {
        content: 'counter(gdi-ordered-list) "."',
        flexShrink: 0,
        flexGrow: 0,
        alignSelf: 'flex-start',
        minWidth: '11px',
        textAlign: 'center'
    },
    '& a': {textDecoration: 'underline'},
    '& a:hover': {textDecoration: 'underline'},
    '& [data-gdi-voucher-code]': {textStyle: 'webH1'},
    // Keep images authored inside a paragraph flowing inline with the surrounding
    // copy (e.g. an inline payment-method logo) and vertically centred to the text.
    '& p img': {display: 'inline-block', verticalAlign: 'middle'}
}

/* Keys from resolveGDIStyles output that are text/font-related and should
 * override the tag-based defaults above when explicitly authored in the CMS.
 * Container-level keys (margin, padding, backgroundColor, maxWidth) are
 * intentionally excluded — those apply at the outer Box level only. */
const TAG_STYLE_OVERRIDE_KEYS = new Set([
    'textStyle',
    'fontSize',
    'lineHeight',
    'fontStyle',
    'fontWeight',
    'textDecoration',
    'textTransform',
    'color',
    'textAlign',
    'textShadow'
])
const TAG_STYLE_SELECTOR = '& p, & h1, & h2, & h3, & h4, & h5, & h6'

const isTextAlignmentLayoutStretchDisabled = (disableStretch, breakpoint) => {
    if (disableStretch === true) return true
    if (!disableStretch || typeof disableStretch !== 'object' || Array.isArray(disableStretch)) {
        return false
    }

    return disableStretch[breakpoint] === true
}

const getTextAlignmentLayoutStyles = (textAlign, disableStretch) => {
    if (!textAlign || disableStretch === true) return {}

    if (typeof textAlign === 'object' && !Array.isArray(textAlign)) {
        const breakpoints = Object.keys(textAlign).filter(
            (bp) => textAlign[bp] && !isTextAlignmentLayoutStretchDisabled(disableStretch, bp)
        )
        if (breakpoints.length === 0) return {}

        return {
            alignSelf: Object.fromEntries(breakpoints.map((bp) => [bp, 'stretch'])),
            width: Object.fromEntries(breakpoints.map((bp) => [bp, '100%']))
        }
    }

    return {
        alignSelf: 'stretch',
        width: '100%'
    }
}

const getTextContentWithoutNode = (element) => {
    const clone = element.cloneNode(true)
    clone.querySelector('[data-gdi-readmore]')?.remove()
    return clone.textContent?.trim() || ''
}

const isMeaningfulElement = (element) => {
    if (!element || element.matches?.('[data-gdi-readmore]')) return false
    return Boolean(element.textContent?.trim() || element.children.length > 0)
}

const getNextMeaningfulElement = (element) => {
    let current = element?.nextElementSibling || null
    while (current && !isMeaningfulElement(current)) {
        current = current.nextElementSibling
    }
    return current
}

const wrapFollowingContent = (marker) => {
    const parent = marker.parentNode
    if (!parent) return null

    const wrapper = document.createElement('div')
    let current = marker.nextSibling
    while (current) {
        const next = current.nextSibling
        wrapper.appendChild(current)
        current = next
    }

    if (!isMeaningfulElement(wrapper)) return null
    parent.insertBefore(wrapper, marker.nextSibling)
    return wrapper
}

const resolveReadMoreTarget = (marker, root) => {
    const parent = marker.parentElement
    if (!parent) return null

    if (parent !== root && getTextContentWithoutNode(parent)) {
        return parent
    }

    return (
        getNextMeaningfulElement(parent === root ? marker : parent) || wrapFollowingContent(marker)
    )
}

const GDITextBlock = ({
    HideBlockOn,
    TextForWeb,
    // TextForApp is App-only (consumed by the native app shell) — drop here so it
    // doesn't leak to the DOM as a `textforapp=""` attribute on web.
    TextForApp: _TextForApp, // eslint-disable-line @typescript-eslint/no-unused-vars
    TextAnchorId,
    GDITextBlockStylesPerViewType,
    disableTextAlignmentLayoutStretch = false,
    // SSR-injected map of categoryId → SCAPI c_relativeUrl, populated by
    // `injectCategoryUrls` in ssr.js for any `{link: {linkType: 'category', ...}}`
    // shortcodes embedded in this block's TextForWeb. Destructured here so it
    // doesn't leak to the DOM as a `_resolvedcategoryurls=""` attribute.
    _resolvedCategoryUrls,
    ...props
}) => {
    // All hooks must be called unconditionally before any early returns.
    const intl = useIntl()
    const {brandId} = useBrand()
    const {site, buildUrl} = useMultiSite()
    const {data: customer} = useCurrentCustomer()
    const isRegistered = customer?.isRegistered ?? false
    const {
        pdpLoyaltyPoints,
        loyaltyCustomerFirstName,
        loyaltyMissingQualifyingPoints,
        loyaltyNextTier,
        loyaltyDueDate,
        searchTerm,
        closeParentModal
    } = useGDIDynamicContentContext()
    const rewardTile = useGDIRewardTileContext()
    const loyaltyVoucherCode = rewardTile?.voucherCode || null
    const rewardStartDate = rewardTile?.startDate || null
    const rewardEndDate = rewardTile?.endDate || null
    const {showCustomToast} = useCustomToast() || {}
    const innerRef = useRef(null)
    const [tooltipPortals, setTooltipPortals] = useState([])
    const [voucherPortals, setVoucherPortals] = useState([])
    const [newsletterFormPortals, setNewsletterFormPortals] = useState([])
    const [mapPortals, setMapPortals] = useState([])
    const [dividerPortals, setDividerPortals] = useState([])
    const [rangeMarkerPortals, setRangeMarkerPortals] = useState([])
    // readMore state — derived from the {readMore: {lines, moreLabel, lessLabel}}
    // shortcode marker detected in processedText after each render.
    const [readMoreConfig, setReadMoreConfig] = useState(null)
    // expanded: whether the content is fully revealed
    const [expanded, setExpanded] = useState(false)
    // fullHeight: measured scrollHeight of the content at full expansion (px)
    const [fullHeight, setFullHeight] = useState(0)
    // transitionsReady: disabled on initial collapse to avoid animated snap
    const [transitionsReady, setTransitionsReady] = useState(false)

    // Compute processedText unconditionally so it can be a useEffect dependency.
    const brandName = brandId ? brandId.charAt(0).toUpperCase() + brandId.slice(1) : undefined
    // Inline dynamic tags supplied by GDIDynamicContentProvider. Each token is
    // replaced only when the provider supplies a non-null value, so authors can
    // safely embed `{tag}` references that resolve only in the right page context
    // (e.g. {pdpLoyaltyPoints} on PDP, {loyalty*} on the my-rewards page).
    //
    // `loyaltyVoucherCode` is special: it expands to the voucher text followed
    // by an empty marker span that the post-render effect below hydrates into a
    // copy button via a React portal. The marker keeps the parser pipeline pure
    // (string-in / string-out) while still allowing a real React click handler.
    const escapeHtmlAttr = (value) =>
        String(value)
            .replace(/&/g, '&amp;')
            .replace(/"/g, '&quot;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
    const dynamicTokens = {
        pdpLoyaltyPoints,
        loyaltyCustomerFirstName,
        loyaltyMissingQualifyingPoints,
        loyaltyNextTier,
        loyaltyDueDate,
        searchTerm,
        loyaltyVoucherCode: loyaltyVoucherCode
            ? `<span data-gdi-voucher style="display:inline-flex;align-items:center;gap:16px;justify-content:center;width:100%;">` +
              `<span data-gdi-voucher-code>${escapeHtmlAttr(loyaltyVoucherCode)}</span>` +
              `<span style="line-height:0;" data-gdi-voucher-copy="${escapeHtmlAttr(
                  loyaltyVoucherCode
              )}"></span>` +
              `</span>`
            : null,
        // `{newsletterForm}` always expands to an empty marker span. The
        // post-render effect below queries every `[data-gdi-newsletter-form]`
        // node and mounts the NewsletterFormInline React component into it
        // via a portal, so the form keeps its real React lifecycle, hooks
        // and submit logic while the surrounding HTML stays string-only.
        newsletterForm: `<span data-gdi-newsletter-form></span>`
    }
    let textWithTokens = TextForWeb
    if (textWithTokens) {
        for (const [key, value] of Object.entries(dynamicTokens)) {
            if (value != null) {
                textWithTokens = textWithTokens.replaceAll(`{${key}}`, value)
            }
        }
        if (rewardStartDate) {
            textWithTokens = textWithTokens.replaceAll('[Start date]', rewardStartDate)
        }
        if (rewardEndDate) {
            textWithTokens = textWithTokens.replaceAll('[End date]', rewardEndDate)
        }
    }
    const googleMapsApiKey = getSiteEnvVarValue(getConfig()?.app, 'GOOGLE_MAPS_API_KEY', site?.id) || null
    const processedText = textWithTokens
        ? parseShortcodes(textWithTokens, {
              brandName,
              isRegistered,
              googleMapsApiKey,
              categoryUrls: _resolvedCategoryUrls,
              buildUrl: (path) => localizeHref(path, buildUrl)
          })
        : null

    // After each render, find tooltip and readMore marker spans and hydrate them.
    useEffect(() => {
        if (!innerRef.current || !processedText) {
            setTooltipPortals([])
            setVoucherPortals([])
            setNewsletterFormPortals([])
            setMapPortals([])
            setDividerPortals([])
            setRangeMarkerPortals([])
            setReadMoreConfig(null)
            return
        }
        // ── Tooltip portals ──
        const tooltipSpans = Array.from(innerRef.current.querySelectorAll('[data-gdi-tooltip]'))
        setTooltipPortals(
            tooltipSpans.length === 0
                ? []
                : tooltipSpans.map((span, i) => ({
                      key: i,
                      container: span,
                      content: span.getAttribute('data-gdi-tooltip') || '',
                      label: span.getAttribute('data-gdi-tooltip-label') || null
                  }))
        )
        // ── Voucher-code copy buttons ──
        const voucherSpans = Array.from(
            innerRef.current.querySelectorAll('[data-gdi-voucher-copy]')
        )
        setVoucherPortals(
            voucherSpans.length === 0
                ? []
                : voucherSpans.map((span, i) => ({
                      key: `voucher-${i}`,
                      container: span,
                      code: span.getAttribute('data-gdi-voucher-copy') || ''
                  }))
        )
        // ── Newsletter inline form ──
        const newsletterSpans = Array.from(
            innerRef.current.querySelectorAll('[data-gdi-newsletter-form]')
        )
        setNewsletterFormPortals(
            newsletterSpans.length === 0
                ? []
                : newsletterSpans.map((span, i) => ({
                      key: `newsletter-${i}`,
                      container: span
                  }))
        )
        // ── Inline map portals ──
        const mapSpans = Array.from(innerRef.current.querySelectorAll('[data-gdi-map]'))
        setMapPortals(
            mapSpans.length === 0
                ? []
                : mapSpans.map((span, i) => {
                      let cfg = {}
                      try {
                          cfg = JSON.parse(span.getAttribute('data-gdi-map') || '{}')
                      } catch (_) {
                          // malformed — skip
                      }
                      return {key: `map-${i}`, container: span, cfg}
                  })
        )
        // ── Divider portals ──
        // Each {divider, ...props} shortcode produces a <div data-gdi-divider>
        // marker; render a Chakra <Divider /> portal inside it forwarding the
        // serialised props.
        const dividerNodes = Array.from(
            innerRef.current.querySelectorAll('[data-gdi-divider]')
        )
        setDividerPortals(
            dividerNodes.length === 0
                ? []
                : dividerNodes.map((node, i) => {
                      let cfg = {}
                      try {
                          cfg = JSON.parse(node.getAttribute('data-gdi-divider') || '{}')
                      } catch (_) {
                          // malformed — fall through with empty props
                      }
                      return {key: `divider-${i}`, container: node, cfg}
                  })
        )
        // ── RangeMarker portals ──
        const rangeMarkerNodes = Array.from(
            innerRef.current.querySelectorAll('[data-gdi-range-marker]')
        )
        setRangeMarkerPortals(
            rangeMarkerNodes.length === 0
                ? []
                : rangeMarkerNodes.map((node, i) => {
                      let cfg = {}
                      try {
                          cfg = JSON.parse(node.getAttribute('data-gdi-range-marker') || '{}')
                      } catch (_) {
                          // malformed — render empty
                      }
                      return {key: `range-marker-${i}`, container: node, cfg}
                  })
        )
        // ── Clara-chat links ──
        // `{link: {linkType: 'clara-chat'}, text: '...'}` emits <a data-gdi-clara-chat>.
        // Attach a click handler that programmatically triggers the Clara button.
        const claraChatLinks = Array.from(
            innerRef.current.querySelectorAll('[data-gdi-clara-chat]')
        )
        claraChatLinks.forEach((el) => {
            el.addEventListener('click', (e) => {
                e.preventDefault()
                document
                    .querySelector('.embeddedMessagingConversationButton')
                    ?.click()
            })
        })

        // ── readMore: line-clamp behaviour ──
        const marker = innerRef.current.querySelector('[data-gdi-readmore]')
        if (marker) {
            const lines = Math.max(
                1,
                parseInt(marker.getAttribute('data-gdi-readmore') || '3', 10) || 3
            )
            const moreLabel = marker.getAttribute('data-gdi-readmore-more') || 'Read More'
            const lessLabel = marker.getAttribute('data-gdi-readmore-less') || 'Read Less'
            const target = resolveReadMoreTarget(marker, innerRef.current)

            if (!target) {
                marker.remove()
                setReadMoreConfig(null)
                setTransitionsReady(false)
                return
            }

            const buttonContainer = document.createElement('div')
            buttonContainer.setAttribute('data-gdi-readmore-button', '')
            target.insertAdjacentElement('afterend', buttonContainer)
            // Remove the invisible marker so it doesn’t affect layout/height.
            marker.remove()
            // Measure the FULL content height before applying the collapsed cap.
            // scrollHeight reports the overflowed height regardless of max-height.
            const full = target.scrollHeight
            // Measure the actual rendered line height from the first text-bearing
            // child element (p, h1-h6) so the clamp is pixel-perfect and doesn't
            // bleed a partial next line regardless of the applied text style.
            const textEl = target.matches?.('p, h1, h2, h3, h4, h5, h6')
                ? target
                : target.querySelector('p, h1, h2, h3, h4, h5, h6') || target
            const lineHeight = parseFloat(getComputedStyle(textEl).lineHeight) || 24
            setFullHeight(full)
            setReadMoreConfig({lines, moreLabel, lessLabel, lineHeight, target, buttonContainer})
            setExpanded(false)
            // Disable transitions so the initial collapse is instant (no animation).
            setTransitionsReady(false)
            // Re-enable transitions after two animation frames (one for layout,
            // one for paint) so user interactions animate smoothly.
            requestAnimationFrame(() => requestAnimationFrame(() => setTransitionsReady(true)))
        } else {
            setReadMoreConfig(null)
            setTransitionsReady(false)
        }
    }, [processedText])

    useEffect(() => {
        const target = readMoreConfig?.target
        if (!target) return undefined

        target.style.maxHeight = expanded
            ? `${fullHeight}px`
            : `${readMoreConfig.lines * readMoreConfig.lineHeight}px`
        target.style.overflow = 'hidden'
        target.style.transition = transitionsReady ? 'max-height 0.35s ease' : 'none'

        return () => {
            target.style.maxHeight = ''
            target.style.overflow = ''
            target.style.transition = ''
        }
    }, [expanded, fullHeight, readMoreConfig, transitionsReady])

    const hideStyles = getGDIHideStyles(HideBlockOn)
    if (hideStyles.doNotLoadComponent) return null

    const {styles: responsiveStyles, hoverStyles} = resolveGDIStyles(
        GDITextBlockStylesPerViewType,
        GDI_TEXT_BLOCK_STYLE_MAP
    )
    const {styles: fontStyleStyles, hoverStyles: fontStyleHoverStyles} = resolveGDIStyles(
        GDITextBlockStylesPerViewType,
        {FontStyle: 'fontStyle'}
    )

    mergeResolvedStyles(
        responsiveStyles,
        resolveResponsiveFontStyleProps(fontStyleStyles.fontStyle)
    )
    mergeResolvedStyles(
        hoverStyles,
        resolveResponsiveFontStyleProps(fontStyleHoverStyles.fontStyle)
    )

    // When fontSize is a raw pixel/CSS value (not a design-system token — those are
    // routed to `textStyle` by resolveGDIStyles) and no explicit lineHeight was
    // authored, apply a sensible default of 1.2 so the text is not too cramped.
    if (responsiveStyles.fontSize && !responsiveStyles.lineHeight) {
        responsiveStyles.lineHeight = 1.2
    }
    mergeResolvedStyles(
        responsiveStyles,
        getTextAlignmentLayoutStyles(responsiveStyles.textAlign, disableTextAlignmentLayoutStretch)
    )

    /* Extract only the text/font-related authored styles so they can be
     * re-applied explicitly on every tag selector, overriding the defaults
     * from TAG_TEXT_STYLES via CSS cascade (same specificity, later rule wins). */
    const tagStyleOverrides = Object.fromEntries(
        Object.entries(responsiveStyles).filter(([k]) => TAG_STYLE_OVERRIDE_KEYS.has(k))
    )
    const hasTagStyleOverrides = Object.keys(tagStyleOverrides).length > 0

    /* Build dynamic sx rules for any {textStyle: token} shortcodes in the content.
     * The parser emits data-gdi-textstyle="token" on the wrapping span; here we
     * generate a Chakra sx selector for each unique token found in the HTML.
     * Use `inline-flex` (not `flex`) so the span stays in the text flow when
     * the directive is authored inline next to surrounding copy — e.g.
     * "...refer to our {link: ..., text: 'Terms'} and Conditions..." should
     * render on one continuous line, not break before/after the link. */
    const textStyleRules = (() => {
        if (!processedText) return {}
        const rules = {}
        const re = /data-gdi-textstyle="([^"]+)"/g
        let m
        while ((m = re.exec(processedText)) !== null) {
            rules[`& [data-gdi-textstyle="${m[1]}"]`] = {
                textStyle: m[1],
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
            }
        }
        return rules
    })()

    if (!TextForWeb) return null

    const {sx: propsSx, ...restProps} = props
    const domRestProps = pickGDIDomProps(restProps)
    const {directStyles, sxStyles: responsiveSx} = splitGDIResponsiveStyles(responsiveStyles)
    const desktopHoverSx = getGDIDesktopHoverSx(hoverStyles)

    /* An inline {map: …} with an authored fixed width sizes this block by its own
     * content, so the block itself overflows a narrower column and the span's
     * max-width:100% resolves against an already-blown-out parent. Clamping the
     * block to its column breaks that loop. Skipped when the CMS authors a width
     * constraint of its own. */
    const inlineMapClampSx =
        processedText?.includes('data-gdi-map') && !responsiveStyles.maxWidth
            ? {maxWidth: '100%'}
            : undefined
    const outerSx = mergeGDISx(
        inlineMapClampSx,
        responsiveSx,
        desktopHoverSx,
        hideStyles.sx,
        propsSx
    )
    const tagOverrideSx = getGDINestedResponsiveSx(TAG_STYLE_SELECTOR, tagStyleOverrides)

    return (
        <Box
            data-block-type="GDITextBlock"
            id={TextAnchorId || undefined}
            sx={outerSx}
            {...directStyles}
            {...domRestProps}
        >
            {/* Content — clamped to N lines when readMoreConfig is active */}
            <Box
                ref={innerRef}
                dangerouslySetInnerHTML={{__html: processedText}}
                sx={{
                    ...TAG_TEXT_STYLES,
                    ...GDI_INLINE_MAP_SX,
                    ...textStyleRules,
                    ...(hasTagStyleOverrides && tagOverrideSx)
                }}
            />
            {readMoreConfig?.buttonContainer &&
                ReactDOM.createPortal(
                    <Button
                        variant="unstyled"
                        display="block"
                        textStyle="bodyXsLink"
                        lineHeight={'18px'}
                        h={'auto'}
                        w={'auto'}
                        mt={3}
                        onClick={() => setExpanded((v) => !v)}
                    >
                        <Text as={'span'} textStyle="bodyXsLink">
                            {expanded ? readMoreConfig.lessLabel : readMoreConfig.moreLabel}
                        </Text>
                    </Button>,
                    readMoreConfig.buttonContainer
                )}
            {tooltipPortals.map(({key, container, content, label}) =>
                ReactDOM.createPortal(
                    <Tooltip variant="info" label={content} placement="top" hasArrow={false}>
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                verticalAlign: 'middle'
                            }}
                        >
                            {label && <span dangerouslySetInnerHTML={{__html: label}} />}
                            <IconButton
                                variant="unstyled"
                                display="flex"
                                minW="auto"
                                h="20px"
                                width="20px"
                                aria-label={`More information: ${content}`}
                                icon={<InfoCircle width="16px" height="16px" />}
                            />
                        </span>
                    </Tooltip>,
                    container,
                    key
                )
            )}
            {voucherPortals.map(({key, container, code}) => {
                const handleCopy = async () => {
                    try {
                        if (navigator?.clipboard?.writeText) {
                            await navigator.clipboard.writeText(code)
                        } else if (typeof document !== 'undefined') {
                            // Legacy fallback for browsers without the async Clipboard API.
                            const textarea = document.createElement('textarea')
                            textarea.value = code
                            textarea.setAttribute('readonly', '')
                            textarea.style.position = 'absolute'
                            textarea.style.left = '-9999px'
                            document.body.appendChild(textarea)
                            textarea.select()
                            document.execCommand('copy')
                            document.body.removeChild(textarea)
                        }
                    } catch {
                        // Swallow clipboard errors silently; the toast still fires so
                        // the customer knows the action was acknowledged.
                    }
                    showCustomToast?.({
                        message: intl.formatMessage(voucherMessages.promoCodeCopied)
                    })
                }
                return ReactDOM.createPortal(
                    <IconButton
                        variant="unstyled"
                        minW="auto"
                        h="24px"
                        width="24px"
                        display="inline-flex"
                        verticalAlign="middle"
                        aria-label={intl.formatMessage(voucherMessages.copyAriaLabel, {code})}
                        onClick={handleCopy}
                        icon={<CopyIcon boxSize="24px" />}
                    />,
                    container,
                    key
                )
            })}
            {newsletterFormPortals.map(({key, container}) =>
                ReactDOM.createPortal(
                    <NewsletterFormInline
                        customer={customer}
                        onSuccess={() => {
                            showCustomToast?.({
                                message: intl.formatMessage(newsletterMessages.subscribedToast)
                            })
                            closeParentModal?.()
                        }}
                    />,
                    container,
                    key
                )
            )}
            {mapPortals.map(({key, container, cfg}) =>
                ReactDOM.createPortal(
                    <GDIInlineMap
                        lat={cfg.lat}
                        lng={cfg.lng}
                        place={cfg.place}
                        zoom={cfg.zoom}
                        apiKey={googleMapsApiKey}
                        container={container}
                    />,
                    container,
                    key
                )
            )}
            {dividerPortals.map(({key, container, cfg}) =>
                ReactDOM.createPortal(<Divider {...cfg} />, container, key)
            )}
            {rangeMarkerPortals.map(({key, container, cfg}) =>
                ReactDOM.createPortal(
                    <GDIRangeMarker labels={cfg.labels} value={cfg.value} color={cfg.color} my={cfg.my} mt={cfg.mt} mb={cfg.mb} mx={cfg.mx} />,
                    container,
                    key
                )
            )}
        </Box>
    )
}

GDITextBlock.propTypes = {
    HideBlockOn: PropTypes.string,
    TextForWeb: PropTypes.string,
    TextForApp: PropTypes.string,
    TextAnchorId: PropTypes.string,
    GDITextBlockStylesPerViewType: PropTypes.array,
    GDITextBlockAudienceTargeting: PropTypes.object,
    disableTextAlignmentLayoutStretch: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    _resolvedCategoryUrls: PropTypes.objectOf(PropTypes.string)
}

export default GDITextBlock
